import { useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Divider,
  Input,
  Rating,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

// Utils
import { formatPhone } from '../../../utils/formatter';
import messages from '../../../static/messages';

// Services
import clientsServices from '../../../services/clientsServices';

// Styles
import { button } from '../../../static/theme-styles';

// Mobile Info Row Component
const MobileInfoRow = ({ icon: Icon, label, value, action, actionColor = '#1976d2' }) => {
  if (!value && !action) return null;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      py: 1.5,
      borderBottom: '1px solid #f0f0f0',
    }}>
      {Icon && <Icon sx={{ color: '#757575', fontSize: 20, mr: 1.5 }} />}
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        {action ? (
          action
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {value || '—'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ClientDetailsTable = ({ client, id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const confirm = useConfirm();

  const handleRateClient = (event) => {
    confirm({
      title: messages.confirm_message.update,
      description: messages.confirm_message.update_description('Client Rating'),
    })
      .then(() => rateClient(event.target.value))
      .catch(() => toast.info(messages.cancel_message));
  };

  const rateClient = async (rating) => {
    try {
      const response = await clientsServices.update(client.id, { rating });

      if (response.status === 200) {
        refresh();
        toast.success(messages.client.update);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Mobile View
  if (isMobile) {
    return (
      <Fragment>
        <Box>
          {/* Rating */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            py: 2,
            borderBottom: '1px solid #f0f0f0',
          }}>
            <StarIcon sx={{ color: '#ffc107', fontSize: 20, mr: 1.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Client Rating
              </Typography>
              <Rating
                value={client?.rating || 0}
                size="medium"
                onChange={handleRateClient}
              />
            </Box>
          </Box>

          {/* Company */}
          <MobileInfoRow 
            icon={BusinessIcon} 
            label="Company" 
            value={client?.companyName} 
          />

          {/* Name */}
          <MobileInfoRow 
            icon={PersonIcon} 
            label="Full Name" 
            value={`${client?.firstName || ''} ${client?.lastName || ''}`.trim() || null} 
          />

          {/* Phone - Clickable */}
          {client?.phoneNumber && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
            }}>
              <PhoneIcon sx={{ color: '#4caf50', fontSize: 20, mr: 1.5 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Phone
                </Typography>
                <Button
                  component="a"
                  href={`tel:${client.phoneNumber}`}
                  variant="text"
                  size="small"
                  sx={{ p: 0, minWidth: 0, fontWeight: 600, color: '#4caf50' }}
                >
                  {formatPhone(client.phoneNumber)}
                </Button>
              </Box>
            </Box>
          )}

          {/* Fax */}
          {client?.fax && (
            <MobileInfoRow 
              icon={PhoneIcon} 
              label="Fax" 
              value={formatPhone(client.fax)} 
            />
          )}

          {/* Mobile */}
          {client?.mobile && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
            }}>
              <PhoneIcon sx={{ color: '#2196f3', fontSize: 20, mr: 1.5 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Mobile
                </Typography>
                <Button
                  component="a"
                  href={`tel:${client.mobile}`}
                  variant="text"
                  size="small"
                  sx={{ p: 0, minWidth: 0, fontWeight: 600, color: '#2196f3' }}
                >
                  {formatPhone(client.mobile)}
                </Button>
              </Box>
            </Box>
          )}

          {/* Email - Clickable */}
          {client?.email && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
            }}>
              <EmailIcon sx={{ color: '#1976d2', fontSize: 20, mr: 1.5 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Email
                </Typography>
                <Button
                  component="a"
                  href={`mailto:${client.email}`}
                  variant="text"
                  size="small"
                  sx={{ p: 0, minWidth: 0, fontWeight: 600, color: '#1976d2', textTransform: 'none' }}
                >
                  {client.email}
                </Button>
              </Box>
            </Box>
          )}

          {/* Created By */}
          {client?.createdBy && (
            <MobileInfoRow 
              icon={PersonOutlineIcon} 
              label="Created By" 
              value={client.createdBy.fullName || `${client.createdBy.firstName || ''} ${client.createdBy.lastName || ''}`.trim()} 
            />
          )}
        </Box>

        <ClientNotes {...{ notesList: client?.notes, id, refresh }} />
      </Fragment>
    );
  }

  // Desktop View - Original Table
  return (
    <Fragment>
      <Table>
        <TableBody>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>Client Rating</TableCell>
            <TableCell>
              <Rating
                value={client?.rating}
                size="large"
                onChange={handleRateClient}
                sx={{ display: 'flex', alignItems: 'center' }}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
            <TableCell>{client?.companyName}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
            <TableCell>{client?.firstName}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
            <TableCell>{client?.lastName}</TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
            <TableCell>{formatPhone(client?.phoneNumber)}</TableCell>
          </TableRow>
          {client?.fax && (
            <TableRow sx={{ backgroundColor: '#fff' }}>
              <TableCell sx={{ fontWeight: 600 }}>Fax</TableCell>
              <TableCell>{formatPhone(client?.fax)}</TableCell>
            </TableRow>
          )}
          {client?.mobile && (
            <TableRow sx={{ backgroundColor: '#fff' }}>
              <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
              <TableCell>{formatPhone(client?.mobile)}</TableCell>
            </TableRow>
          )}
          <TableRow sx={{ backgroundColor: '#fff' }}>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell>{client?.email}</TableCell>
          </TableRow>
          {client?.createdBy && (
            <TableRow sx={{ backgroundColor: '#fff' }}>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
              <TableCell>{client.createdBy.fullName || `${client.createdBy.firstName || ''} ${client.createdBy.lastName || ''}`.trim()}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ClientNotes {...{ notesList: client?.notes, id, refresh }} />
    </Fragment>
  );
};

export default ClientDetailsTable;

const ClientNotes = ({ notesList, id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const confirm = useConfirm();
  const userId = JSON.parse(localStorage.getItem('user')).id;
  const userRole = JSON.parse(localStorage.getItem('user')).role;
  const [comment, setComment] = useState();
  const [editableNote, setEditableNote] = useState();

  // Action functions
  const onEditNote = (el) => setEditableNote(el);

  const onCancelEditNote = () => {
    confirm({
      title: messages.confirm_message.close_drawer,
      description: messages.confirm_message.close_drawer_description,
    })
      .then(() => setEditableNote(null))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Handler functions
  const handleChangeNote = (e) => {
    setEditableNote((prevState) => ({
      ...prevState,
      note: e.target.value,
    }));
  };

  const handleRemove = (noteId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Note'),
    })
      .then(() => removeNote(noteId))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const addNewNote = async () => {
    const notes = [
      ...notesList,
      {
        note: comment,
      },
    ];

    try {
      const response = await clientsServices.update(id, { notes });

      if (response.status === 200) {
        setComment(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateNote = async () => {
    const notes = [
      ...notesList.filter((el) => el.id !== editableNote.id),
      {
        id: editableNote.id,
        note: editableNote.note,
      },
    ];

    try {
      const response = await clientsServices.update(id, { notes });

      if (response.status === 200) {
        setEditableNote(null);
        toast.success(messages.success.update.note.save);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeNote = async (noteId) => {
    const notes = [...notesList.filter((el) => el.id !== noteId)];

    try {
      const response = await clientsServices.update(id, { notes });

      if (response.status === 200) {
        toast.success(messages.success.update.note.remove);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  return (
    <Box sx={{ pt: 3, pb: 3 }}>
      <Typography sx={{ fontWeight: 600, mb: 2 }}>📝 Notes:</Typography>
      
      {notesList.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No notes yet
        </Typography>
      ) : (
        notesList.map((el) => (
          <Card key={el.id} variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              {editableNote?.id === el.id ? (
                <Box>
                  <TextField
                    defaultValue={editableNote.note}
                    name="editableNote"
                    id="editableNote"
                    onChange={handleChangeNote}
                    autoFocus
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      onClick={updateNote}
                      startIcon={<SaveIcon />}
                    >
                      Save
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={onCancelEditNote}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {el.note}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      {el.createdBy?.fullName || 'Unknown'} • {moment(el.createdAt).tz('America/Los_Angeles').format('L')}
                    </Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => onEditNote(el)}
                        disabled={userId !== el.createdBy?.id && !['superadmin', 'admin', 'dispatcher'].includes(userRole)}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemove(el.id)}
                        disabled={userId !== el.createdBy?.id && !['superadmin', 'admin', 'dispatcher'].includes(userRole)}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      <Box sx={{ mt: 2 }}>
        <TextField
          id="new-note"
          label="New Note"
          multiline
          rows={isMobile ? 3 : 4}
          value={comment || ''}
          fullWidth
          size={isMobile ? 'small' : 'medium'}
          onChange={(e) => setComment(e.target.value)}
        />

        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'small'}
          fullWidth={isMobile}
          sx={{ ...button('secondary'), mt: 2 }}
          onClick={addNewNote}
          disabled={!comment}
        >
          Add Note
        </Button>
      </Box>
    </Box>
  );
};
